if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.loadRecommendations();
      }

      getSourceProductId() {
        const cartProductIds = (this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        let storedProductId = '';
        try {
          storedProductId = sessionStorage.getItem('cartDrawerRecommendationsProductId') || '';
        } catch (error) {
          storedProductId = '';
        }

        if (storedProductId && (!cartProductIds.length || cartProductIds.includes(storedProductId))) {
          return storedProductId;
        }

        return this.dataset.productId || '';
      }

      getRecommendationsUrl(productId) {
        const root = window.Shopify?.routes?.root || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
        const intent = ['complementary', 'related'].includes(this.dataset.intent)
          ? this.dataset.intent
          : 'complementary';
        const limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 4, 1), 10);
        const params = new URLSearchParams({
          product_id: productId,
          limit: String(limit),
          intent,
        });

        return `${normalizedRoot}recommendations/products.json?${params.toString()}`;
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value || '';
        return div.innerHTML;
      }

      formatMoney(cents) {
        const amount = (parseInt(cents, 10) || 0) / 100;
        const currency = this.dataset.currency || window.Shopify?.currency?.active || 'BRL';

        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
            style: 'currency',
            currency,
          }).format(amount);
        } catch (error) {
          return `R$ ${amount.toFixed(2).replace('.', ',')}`;
        }
      }

      getProductImage(product) {
        if (!product) return '';
        if (typeof product.featured_image === 'string') return product.featured_image;
        if (product.featured_image?.src) return product.featured_image.src;
        if (Array.isArray(product.images) && product.images.length) return product.images[0];
        return '';
      }

      getProductUrl(product) {
        const root = window.Shopify?.routes?.root || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

        if (product.url) return product.url;
        if (product.handle) return `${normalizedRoot}products/${product.handle}`;
        return '#';
      }

      isSimpleProduct(product, variant) {
        if (!product || !variant) return false;
        if (product.has_only_default_variant === true) return true;
        if (product.variants?.length === 1 && variant.title === 'Default Title') return true;
        return false;
      }

      renderBagIcon() {
        return `
          <svg aria-hidden="true" focusable="false" class="cart-drawer-recommendation-card__button-icon" viewBox="0 0 24 24">
            <path d="M6.75 8.25h10.5l.75 11.25H6L6.75 8.25Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
            <path d="M9 8.25V6.75a3 3 0 0 1 6 0v1.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
          </svg>
        `;
      }

      renderCard(product, index) {
        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (!variant) return '';

        const image = this.getProductImage(product);
        const productUrl = this.getProductUrl(product);
        const productTitle = this.escapeHtml(product.title);
        const recommendationTitle = this.escapeHtml(this.dataset.heading || 'Leve tambem!');
        const price = variant.price || product.price;
        const compareAtPrice = variant.compare_at_price || product.compare_at_price;
        const isAvailable = Boolean(variant.available);
        const canAddToCart = isAvailable && this.isSimpleProduct(product, variant);
        const buttonLabel = this.escapeHtml(this.dataset.addLabel || 'Adicionar');
        const buttonIcon = this.renderBagIcon();
        const buttonMarkup = canAddToCart
          ? `
            <product-form class="cart-drawer-recommendation-card__form">
              <form method="post" action="${window.routes?.cart_add_url || '/cart/add'}" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
                <input type="hidden" name="id" value="${variant.id}">
                <button type="submit" name="add" class="cart-drawer-recommendation-card__button" aria-label="${this.escapeHtml(`${this.dataset.addLabel || 'Adicionar'} ${product.title}`)}">
                  <span class="visually-hidden">${buttonLabel}</span>
                  ${buttonIcon}
                  <div class="loading__spinner hidden">
                    <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </button>
              </form>
            </product-form>`
          : isAvailable
            ? `<a href="${this.escapeHtml(productUrl)}" class="cart-drawer-recommendation-card__button" aria-label="${this.escapeHtml(`${this.dataset.viewOptionsLabel || 'Ver opcoes'} ${product.title}`)}"><span class="visually-hidden">${this.escapeHtml(this.dataset.viewOptionsLabel || 'Ver opcoes')}</span>${buttonIcon}</a>`
            : `<span class="cart-drawer-recommendation-card__button cart-drawer-recommendation-card__button--disabled">${this.escapeHtml(this.dataset.soldOutLabel || 'Esgotado')}</span>`;

        return `
          <li id="Slide-CartDrawerRecommendations-${index + 1}" class="cart-drawer-recommendations__slide grid__item slider__slide">
            <article class="cart-drawer-recommendation-card">
              <p class="cart-drawer-recommendation-card__eyebrow">${recommendationTitle}</p>
              <div class="cart-drawer-recommendation-card__main">
                <a href="${this.escapeHtml(productUrl)}" class="cart-drawer-recommendation-card__media" tabindex="-1" aria-hidden="true">
                  ${
                    image
                      ? `<img src="${this.escapeHtml(image)}" alt="${productTitle}" loading="lazy" width="64" height="64">`
                      : '<svg class="placeholder-svg" viewBox="0 0 525.5 525.5" xmlns="http://www.w3.org/2000/svg" role="presentation"></svg>'
                  }
                </a>

                <div class="cart-drawer-recommendation-card__content">
                  <a href="${this.escapeHtml(productUrl)}" class="cart-drawer-recommendation-card__title">${productTitle}</a>

                  <div class="cart-drawer-recommendation-card__price">
                    ${compareAtPrice && compareAtPrice > price ? `<s>${this.formatMoney(compareAtPrice)}</s>` : ''}
                    <span>${this.formatMoney(price)}</span>
                  </div>
                </div>

                ${buttonMarkup}
              </div>
            </article>
          </li>
        `;
      }

      renderProducts(products) {
        const cartProductIds = (this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        const recommendedProducts = products.filter((product) => !cartProductIds.includes(String(product.id)));
        const cards = recommendedProducts
          .map((product, index) => this.renderCard(product, index))
          .join('')
          .trim();

        if (!cards) return '';

        const sliderId = 'Slider-CartDrawerRecommendations';
        const configuredLimit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 4, 1), 10);
        const showControls = configuredLimit > 1 && recommendedProducts.length > 1;

        return `
          <div class="cart-drawer-recommendations__inner">
            <slider-component class="cart-drawer-recommendations__slider">
              <ul id="${sliderId}" class="cart-drawer-recommendations__list grid grid--1-col slider slider--everywhere" role="list" aria-label="${this.escapeHtml(this.dataset.heading || 'Produtos recomendados')}">
                ${cards}
              </ul>
              ${
                showControls
                  ? `
                    <div class="cart-drawer-recommendations__controls slider-buttons">
                      <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Slide anterior" aria-controls="${sliderId}">
                        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                        </svg>
                      </button>
                      <div class="slider-counter caption">
                        <span class="slider-counter--current">1</span>
                        <span aria-hidden="true"> / </span>
                        <span class="visually-hidden">de</span>
                        <span class="slider-counter--total">${recommendedProducts.length}</span>
                      </div>
                      <button type="button" class="slider-button slider-button--next" name="next" aria-label="Proximo slide" aria-controls="${sliderId}">
                        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                        </svg>
                      </button>
                    </div>
                  `
                  : ''
              }
            </slider-component>
          </div>
        `;
      }

      loadRecommendations() {
        const productId = this.getSourceProductId();

        if (!productId) {
          this.hidden = true;
          return;
        }

        this.classList.add('is-loading');

        fetch(this.getRecommendationsUrl(productId))
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.json();
          })
          .then((responseJson) => {
            const content = this.renderProducts(responseJson.products || []);

            if (!content) {
              this.hidden = true;
              return;
            }

            this.innerHTML = content;
            this.hidden = false;
            this.classList.add('is-loaded');
          })
          .catch(() => {
            this.hidden = true;
          })
          .finally(() => {
            this.classList.remove('is-loading');
          });
      }
    }
  );
}
